// Theatre setup: the rooms, the vocabularies, the room blocks, the billing
// parameters, and the maintenance log.
//
// Nothing here is ever deleted. A retired room or vocabulary entry is
// deactivated, because closed cases reference it and a theatre register has to
// stay readable years later.
//
// Department and the medical-service (procedure) master are NOT here — OT reuses
// the EMR's own, so surgeries reconcile with the rest of the system.

import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import {
  IPD_COLORS,
  IpdButton,
  IpdFormField,
  IpdSectionCard,
  IpdTabBar,
  IpdTextField,
} from "../../ipd/utils";
import { formatIstDateTime } from "../../../utils/istDateTime";
import {
  addAnaesthetist,
  cancelRoomBlock,
  createAttendeeType,
  createBillingParameter,
  createRoom,
  createRoomBlock,
  createVocab,
  deactivateAttendeeType,
  deactivateBillingParameter,
  deactivateRoom,
  deactivateVocab,
  fetchAnaesthetists,
  fetchBillingParameters,
  fetchEnvironmentLogs,
  fetchMaintenanceLogs,
  fetchRoomBlocks,
  fetchSetup,
  createNoteTemplate,
  fetchNoteTemplates,
  recordEnvironmentReading,
  retireNoteTemplate,
  recordMaintenance,
  removeAnaesthetist,
} from "../api/otApi";
import OtScreen from "../components/OtScreen";
import StaffPicker from "../components/StaffPicker";
import { OtSelect, errorMessage, report } from "../components/common";
import { CLASSIFICATION_OT_ROOMS, MAINTENANCE_TYPES, humanise } from "../constants";
import { applyStaffSelection } from "../utils/staffSelection";
import ChargeServiceAutocomplete from "../../ot-charge-capture/ChargeServiceAutocomplete";

const TABS = [
  { id: "rooms", label: "Operating Suites" },
  { id: "vocab", label: "Clinical Vocabularies" },
  { id: "attendee", label: "Surgical Team Roles" },
  { id: "anaesthetists", label: "Anesthesiologist Roster" },
  { id: "blocks", label: "Theatre Maintenance Blocks" },
  { id: "billing", label: "Department Tariffs" },
  { id: "maintenance", label: "Sterilization & Fumigation Log" },
  { id: "environment", label: "Environmental Monitoring Log" },
  { id: "templates", label: "Clinical Note Templates" },
];

export default function OtMasters() {
  const [tab, setTab] = useState("rooms");
  const [setup, setSetup] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [parameters, setParameters] = useState([]);
  const [logs, setLogs] = useState([]);
  const [environment, setEnvironment] = useState(null);
  const [templates, setTemplates] = useState(null);
  const [anaesthetists, setAnaesthetists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const results = await Promise.allSettled([
        fetchSetup(),
        fetchRoomBlocks({ active_only: 1 }),
        fetchBillingParameters(),
        fetchMaintenanceLogs(),
        // Today only. The register's purpose here is "has this theatre been
        // read today"; the historical view is the report, not this screen.
        fetchEnvironmentLogs(),
        fetchNoteTemplates(),
        fetchAnaesthetists(),
      ]);
      const [s, b, p, l, e, t, a] = results.map((r) =>
        r.status === "fulfilled" ? r.value : null
      );
      if (s) {
        setSetup(s);
      } else {
        setError("Could not load the primary OT setup");
      }
      setBlocks(b || []);
      setParameters(p || []);
      setLogs(l || []);
      setEnvironment(e || null);
      setTemplates(t || null);
      setAnaesthetists(a || []);
    } catch (err) {
      setError(errorMessage(err, "Could not load the OT setup"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rooms = setup?.operating_rooms || [];

  return (
    <OtScreen
      title="Operating Theatre Master Settings"
      description="Operating theatre suites, surgical clinical vocabularies, and regulatory configuration."
      loading={loading}
      error={error}
      onRetry={load}
    >
      <IpdTabBar
        tabs={TABS}
        activeTab={tab}
        onChange={setTab}
        ariaLabel="Theatre setup sections"
      />

      {tab === "rooms" && <RoomsTab rooms={rooms} departments={setup?.departments || []} onDone={load} />}
      {tab === "vocab" && <VocabTab setup={setup} onDone={load} />}
      {tab === "attendee" && <AttendeeTab types={setup?.attendee_types || []} onDone={load} />}
      {tab === "anaesthetists" && (
        <AnaesthetistTab anaesthetists={anaesthetists} onDone={load} />
      )}
      {tab === "blocks" && <BlocksTab blocks={blocks} rooms={rooms} onDone={load} />}
      {tab === "billing" && (
        <BillingTab
          parameters={parameters}
          departments={setup?.departments || []}
          onDone={load}
        />
      )}
      {tab === "maintenance" && <MaintenanceTab logs={logs} rooms={rooms} onDone={load} />}
      {tab === "templates" && (
        <TemplatesTab
          templates={templates?.templates || []}
          availableFields={templates?.available_fields || {}}
          onDone={load}
        />
      )}
      {tab === "environment" && (
        <EnvironmentTab environment={environment} rooms={rooms} onDone={load} />
      )}
    </OtScreen>
  );
}

/** A list of rows with an Add button, shared by every master tab. */
function MasterList({ title, subtitle, onAdd, addLabel, children }) {
  return (
    <IpdSectionCard
      title={title}
      subtitle={subtitle}
      actions={
        onAdd && (
          <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={onAdd}>
            {addLabel || "Add"}
          </Button>
        )
      }
    >
      <Box sx={{ p: 2.5 }}>{children}</Box>
    </IpdSectionCard>
  );
}

function Row({ primary, secondary, active = true, onDeactivate, deactivateLabel = "Deactivate" }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: 1,
        borderBottom: `1px solid ${IPD_COLORS.border}`,
        opacity: active ? 1 : 0.55,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{primary}</Typography>
        {secondary && (
          <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>{secondary}</Typography>
        )}
      </Box>
      {!active && <Chip size="small" label="Inactive" />}
      {active && onDeactivate && (
        <Button size="small" color="error" onClick={onDeactivate}>
          {deactivateLabel}
        </Button>
      )}
    </Box>
  );
}

function RoomsTab({ rooms, departments, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    code: "",
    department_id: "",
    open_time: "08:00",
    close_time: "18:00",
    daily_ceiling: "",
    location: "",
  });

  return (
    <MasterList
      title="Operating Theatre Suites"
      subtitle="Daily capacity ceiling serves as a scheduling advisory threshold."
      addLabel="Register Operating Room"
      onAdd={() => setOpen(true)}
    >
      {rooms.length === 0 ? (
        <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13.5 }}>
          No operating theatre suites configured.
        </Typography>
      ) : (
        rooms.map((r) => (
          <Row
            key={r.id}
            active={r.active}
            primary={`${r.name}${r.code ? ` (${r.code})` : ""}`}
            secondary={`${r.department_name || "Department Unassigned"} · ${
              r.open_time ? `${r.open_time}–${r.close_time}` : "Working hours not set"
            }${r.daily_ceiling ? ` · Capacity ceiling: ${r.daily_ceiling}` : ""}${
              r.location ? ` · Location: ${r.location}` : ""
            }`}
            onDeactivate={async () => {
              if (report(await deactivateRoom(r.id))) onDone();
            }}
          />
        ))
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Register Operating Theatre Room</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 0.5 }}>
            <IpdFormField label="Suite Name">
              <IpdTextField
                fullWidth
                autoFocus
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </IpdFormField>
            <IpdFormField label="Room Code">
              <IpdTextField
                fullWidth
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
              />
            </IpdFormField>
            <IpdFormField label="Clinical Department">
              <OtSelect
                placeholder="Select Department"
                value={form.department_id}
                onChange={(e) => setForm((f) => ({ ...f, department_id: e.target.value }))}
                options={departments.map((d) => ({ value: d.id, label: d.name }))}
              />
            </IpdFormField>
            <IpdFormField label="Physical Location">
              <IpdTextField
                fullWidth
                value={form.location}
                onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
              />
            </IpdFormField>
            <IpdFormField label="Opening Time">
              <IpdTextField
                fullWidth
                type="time"
                value={form.open_time}
                onChange={(e) => setForm((f) => ({ ...f, open_time: e.target.value }))}
              />
            </IpdFormField>
            <IpdFormField label="Closing Time">
              <IpdTextField
                fullWidth
                type="time"
                value={form.close_time}
                onChange={(e) => setForm((f) => ({ ...f, close_time: e.target.value }))}
              />
            </IpdFormField>
            <IpdFormField label="Daily Scheduled Case Ceiling">
              <IpdTextField
                fullWidth
                type="number"
                value={form.daily_ceiling}
                onChange={(e) => setForm((f) => ({ ...f, daily_ceiling: e.target.value }))}
              />
            </IpdFormField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <IpdButton
            disabled={!form.name.trim()}
            onClick={async () => {
              if (report(await createRoom(form))) {
                setOpen(false);
                setForm({ ...form, name: "", code: "", location: "" });
                onDone();
              }
            }}
          >
            Save Operating Room
          </IpdButton>
        </DialogActions>
      </Dialog>
    </MasterList>
  );
}

const VOCABS = [
  { key: "anaesthesia_types", label: "Anesthesia Types" },
  { key: "surgery_positions", label: "Surgical Patient Positions" },
  { key: "incision_types", label: "Surgical Incision Classifications" },
];

function VocabTab({ setup, onDone }) {
  const [adding, setAdding] = useState(null); // the vocab key being added to
  const [name, setName] = useState("");

  return (
    <Box sx={{ display: "grid", gap: 2.5 }}>
      {VOCABS.map((v) => (
        <MasterList
          key={v.key}
          title={v.label}
          addLabel="Add Term"
          onAdd={() => {
            setAdding(v.key);
            setName("");
          }}
        >
          {(setup?.[v.key] || []).length === 0 ? (
            <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13.5 }}>
              No vocabulary terms configured.
            </Typography>
          ) : (
            setup[v.key].map((t) => (
              <Row
                key={t.id}
                primary={t.name}
                active={t.active}
                onDeactivate={async () => {
                  if (report(await deactivateVocab(v.key, t.id))) onDone();
                }}
              />
            ))
          )}
        </MasterList>
      ))}

      <Dialog open={Boolean(adding)} onClose={() => setAdding(null)} fullWidth maxWidth="xs">
        <DialogTitle>Add to {VOCABS.find((v) => v.key === adding)?.label || ""}</DialogTitle>
        <DialogContent>
          <IpdFormField label="Term / Classification">
            <IpdTextField
              fullWidth
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </IpdFormField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdding(null)}>Cancel</Button>
          <IpdButton
            disabled={!name.trim()}
            onClick={async () => {
              if (report(await createVocab(adding, { name }))) {
                setAdding(null);
                onDone();
              }
            }}
          >
            Add Term
          </IpdButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function AttendeeTab({ types, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    is_mandatory: false,
    grading_required: false,
    display_order: "0",
  });

  return (
    <MasterList
      title="Surgical Team Roles"
      subtitle="Clinical surgical roles and mandatory staffing requirements per procedure type."
      addLabel="Define Surgical Team Role"
      onAdd={() => setOpen(true)}
    >
      {types.length === 0 ? (
        <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13.5 }}>
          No surgical team roles configured.
        </Typography>
      ) : (
        types.map((t) => (
          <Row
            key={t.id}
            primary={t.name}
            active={t.active}
            secondary={`Display order ${t.display_order}${t.is_mandatory ? " · Mandatory requirement" : ""}${
              t.grading_required ? " · Clinical grade recorded" : ""
            }`}
            onDeactivate={async () => {
              if (report(await deactivateAttendeeType(t.id))) onDone();
            }}
          />
        ))
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Define Surgical Team Role</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 0.5 }}>
            <IpdFormField label="Role Title">
              <IpdTextField
                fullWidth
                autoFocus
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </IpdFormField>
            <IpdFormField label="Display Order">
              <IpdTextField
                fullWidth
                type="number"
                value={form.display_order}
                onChange={(e) => setForm((f) => ({ ...f, display_order: e.target.value }))}
              />
            </IpdFormField>
            <IpdFormField label="Mandatory Roster Requirement">
              <OtSelect
                value={form.is_mandatory ? "yes" : "no"}
                onChange={(e) => setForm((f) => ({ ...f, is_mandatory: e.target.value === "yes" }))}
                options={[
                  { value: "no", label: "Optional / As Required" },
                  { value: "yes", label: "Mandatory (Flag if unassigned)" },
                ]}
              />
            </IpdFormField>
            <IpdFormField label="Clinical Grade / Credential Tracking">
              <OtSelect
                value={form.grading_required ? "yes" : "no"}
                onChange={(e) =>
                  setForm((f) => ({ ...f, grading_required: e.target.value === "yes" }))
                }
                options={[
                  { value: "no", label: "Not Required" },
                  { value: "yes", label: "Record Clinical Grade" },
                ]}
              />
            </IpdFormField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <IpdButton
            disabled={!form.name.trim()}
            onClick={async () => {
              if (report(await createAttendeeType(form))) {
                setOpen(false);
                setForm({ ...form, name: "" });
                onDone();
              }
            }}
          >
            Save Role
          </IpdButton>
        </DialogActions>
      </Dialog>
    </MasterList>
  );
}

function AnaesthetistTab({ anaesthetists, onDone }) {
  // Who may record a PAC clearance (main/ot/sql/036_ot_anaesthetist.sql).
  // Replaces the old ot_pac permission — see that script's header. An empty
  // list here means the facility has not opted in yet, in which case PAC
  // clearance is not restricted to anyone on this list (ot_common.
  // is_anaesthetist's "not configured" rule) — populating it is what turns
  // the restriction on.
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ doctor_id: "", doctor_name: "" });

  return (
    <MasterList
      title="Anesthesiologist Roster"
      subtitle="Physicians credentialed and privileged for Pre-Anesthesia Clearance (PAC) and intra-operative administration."
      addLabel="Privilege Anesthesiologist"
      onAdd={() => {
        setForm({ doctor_id: "", doctor_name: "" });
        setOpen(true);
      }}
    >
      {anaesthetists.filter((a) => a.active).length === 0 ? (
        <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13.5 }}>
          No anesthesiologists privileged in the active roster.
        </Typography>
      ) : (
        anaesthetists
          .filter((a) => a.active)
          .map((a) => (
            <Row
              key={a.id}
              primary={a.doctor_name}
              onDeactivate={async () => {
                if (report(await removeAnaesthetist(a.id))) onDone();
              }}
              deactivateLabel="Revoke Privilege"
            />
          ))
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Privilege Anesthesiologist (PAC Roster)</DialogTitle>
        <DialogContent>
          <IpdFormField label="Attending Physician">
            <StaffPicker
              label="Search Credentialed Anesthesiologists"
              valueName={form.doctor_name}
              onPick={(picked) =>
                setForm((f) => applyStaffSelection(f, "doctor_id", "doctor_name", picked))
              }
            />
          </IpdFormField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <IpdButton
            disabled={!form.doctor_id}
            onClick={async () => {
              if (report(await addAnaesthetist(form))) {
                setOpen(false);
                onDone();
              }
            }}
          >
            Privilege Physician
          </IpdButton>
        </DialogActions>
      </Dialog>
    </MasterList>
  );
}

function BlocksTab({ blocks, rooms, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    operating_room_id: "",
    start_datetime: "",
    end_datetime: "",
    blocked_for: "MAINTENANCE",
    surgeon_name: "",
    reason: "",
  });

  return (
    <MasterList
      title="Operating Theatre Maintenance Blocks"
      subtitle="Renders the operating suite unavailable for bookings during scheduled maintenance, sterilization, or surgeon block allocation."
      addLabel="Schedule Theatre Block"
      onAdd={() => setOpen(true)}
    >
      {blocks.length === 0 ? (
        <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13.5 }}>
          No active operating theatre blocks.
        </Typography>
      ) : (
        blocks.map((b) => (
          <Row
            key={b.id}
            primary={`${rooms.find((r) => r.id === b.operating_room_id)?.name || "Theatre Suite"} — ${
              b.blocked_for === "SURGEON" ? b.surgeon_name || "Surgeon Block" : "Sanitization / Maintenance"
            }`}
            secondary={`${formatIstDateTime(b.start_datetime)} to ${formatIstDateTime(
              b.end_datetime
            )}${b.reason ? ` · ${b.reason}` : ""}`}
            onDeactivate={async () => {
              if (report(await cancelRoomBlock(b.id))) onDone();
            }}
            deactivateLabel="Release Block"
          />
        ))
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Schedule Operating Theatre Closure / Block</DialogTitle>
        <DialogContent>
          <IpdFormField label="Operating Suite">
            <OtSelect
              placeholder="Select Operating Theatre"
              value={form.operating_room_id}
              onChange={(e) => setForm((f) => ({ ...f, operating_room_id: e.target.value }))}
              options={rooms.filter((r) => r.active).map((r) => ({ value: r.id, label: r.name }))}
            />
          </IpdFormField>
          <Box sx={{ mt: 2, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <IpdFormField label="Block Effective Start">
              <IpdTextField
                fullWidth
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={form.start_datetime}
                onChange={(e) => setForm((f) => ({ ...f, start_datetime: e.target.value }))}
              />
            </IpdFormField>
            <IpdFormField label="Block Effective End">
              <IpdTextField
                fullWidth
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={form.end_datetime}
                onChange={(e) => setForm((f) => ({ ...f, end_datetime: e.target.value }))}
              />
            </IpdFormField>
            <IpdFormField label="Block Classification">
              <OtSelect
                value={form.blocked_for}
                onChange={(e) => setForm((f) => ({ ...f, blocked_for: e.target.value }))}
                options={[
                  { value: "MAINTENANCE", label: "Sanitization / Engineering Maintenance" },
                  { value: "SURGEON", label: "Allocated Surgeon Block" },
                ]}
              />
            </IpdFormField>
            {form.blocked_for === "SURGEON" && (
              <IpdFormField label="Allocated Surgeon Name">
                <IpdTextField
                  fullWidth
                  value={form.surgeon_name}
                  onChange={(e) => setForm((f) => ({ ...f, surgeon_name: e.target.value }))}
                />
              </IpdFormField>
            )}
          </Box>
          <Box sx={{ mt: 2 }}>
            <IpdFormField label="Maintenance or Allocation Rationale">
              <IpdTextField
                fullWidth
                value={form.reason}
                onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
              />
            </IpdFormField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <IpdButton
            disabled={!form.operating_room_id || !form.start_datetime || !form.end_datetime}
            onClick={async () => {
              const payload = {
                ...form,
                start_datetime: new Date(form.start_datetime).toISOString(),
                end_datetime: new Date(form.end_datetime).toISOString(),
              };
              if (report(await createRoomBlock(payload))) {
                setOpen(false);
                onDone();
              }
            }}
          >
            Enforce Theatre Block
          </IpdButton>
        </DialogActions>
      </Dialog>
    </MasterList>
  );
}

function BillingTab({ parameters, departments, onDone }) {
  const [open, setOpen] = useState(false);
  const [service, setService] = useState(null);
  const [form, setForm] = useState({
    name: "",
    department_id: "",
    service_code: "",
    medical_service_id: "",
    charge_type: "FIXED",
    revenue_category: "",
    report_category: "",
  });

  // A parameter with a department IS that department's theatre rate, and is
  // what lets theatre time bill itself at check-out. One without a department
  // is ordinary configuration over a service. Two different jobs, so they are
  // shown as two lists rather than one mixed one.
  const rates = parameters.filter((p) => p.department_id);
  const other = parameters.filter((p) => !p.department_id);
  const unmapped = departments.filter(
    (d) => !rates.some((r) => String(r.department_id) === String(d.id)),
  );

  return (
    <MasterList
      title="Department Operating Theatre Tariffs"
      subtitle="Operating theatre time charges by clinical department. Base tariff rates reconcile with EMR service catalog."
      addLabel="Assign Department Tariff"
      onAdd={() => setOpen(true)}
    >
      {rates.length === 0 ? (
        <Box
          sx={{
            p: 1.5,
            mb: 2,
            borderRadius: 1,
            backgroundColor: IPD_COLORS.warning.bg,
            color: IPD_COLORS.warning.text,
            fontSize: 13.5,
          }}
        >
          No clinical department has an active theatre tariff assigned. Automated time-based charge
          capture is currently inactive. Assign a tariff to automatically post room charges upon
          patient checkout from theatre.
        </Box>
      ) : (
        rates.map((p) => (
          <Row
            key={p.id}
            primary={p.department_name || `Department #${p.department_id}`}
            active={p.active}
            secondary={`${p.service_code || "No service code linked"} — Time-tiered tariff (30-min increments based on in-theatre occupancy)`}
            onDeactivate={async () => {
              if (report(await deactivateBillingParameter(p.id))) onDone();
            }}
          />
        ))
      )}

      {rates.length > 0 && unmapped.length > 0 && (
        <Typography sx={{ mt: 1.5, fontSize: 12.5, color: IPD_COLORS.warning.text }}>
          Unassigned Department Tariffs: {unmapped.map((d) => d.name).join(", ")}
        </Typography>
      )}

      {other.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography
            sx={{
              fontSize: 11.5,
              fontWeight: 800,
              letterSpacing: "0.04em",
              color: IPD_COLORS.textSecondary,
              mb: 0.5,
            }}
          >
            ADDITIONAL PERIOPERATIVE SERVICE MAPPINGS
          </Typography>
          {other.map((p) => (
            <Row
              key={p.id}
              primary={`${p.name} · ${p.charge_type}`}
              active={p.active}
              secondary={`${p.service_code || "No service code"}${
                p.revenue_category ? ` · Sub-Account: ${p.revenue_category}` : ""
              }${p.report_category ? ` / ${p.report_category}` : ""}`}
              onDeactivate={async () => {
                if (report(await deactivateBillingParameter(p.id))) onDone();
              }}
            />
          ))}
        </Box>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Assign Department Operating Theatre Tariff</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary, mb: 2 }}>
            Theatre tariffs are determined by surgical specialty and procedure acuity. Once mapped,
            perioperative time charges post to the inpatient billing ledger automatically upon patient
            checkout.
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 0.5 }}>
            <IpdFormField label="Clinical Department">
              <OtSelect
                placeholder="Select Department"
                value={form.department_id}
                onChange={(e) => setForm((f) => ({ ...f, department_id: e.target.value }))}
                options={(departments || []).map((d) => ({ value: d.id, label: d.name }))}
              />
            </IpdFormField>
            <IpdFormField label="Charge Capture Model">
              <OtSelect
                value={form.charge_type}
                onChange={(e) => setForm((f) => ({ ...f, charge_type: e.target.value }))}
                options={[
                  { value: "FIXED", label: "Time-Tiered (Standard)" },
                  { value: "BUNDLED", label: "Procedure Bundled" },
                ]}
              />
            </IpdFormField>
          </Box>
          <Box sx={{ mt: 2 }}>
            <ChargeServiceAutocomplete
              label="Theatre Service Catalog Item"
              required
              placeholder="Search perioperative room services..."
              classificationName={CLASSIFICATION_OT_ROOMS}
              value={service}
              onChange={(item) => {
                setService(item);
                setForm((f) => ({
                  ...f,
                  service_code: item ? item.service_code || "" : "",
                  medical_service_id: item ? item.medical_service_id || item.id : "",
                  name: item
                    ? `${(departments || []).find(
                        (d) => String(d.id) === String(f.department_id),
                      )?.name || "Theatre Suite"} — ${item.name}`
                    : f.name,
                }));
              }}
            />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 2 }}>
            <IpdFormField label="Revenue Sub-Account Code">
              <IpdTextField
                fullWidth
                value={form.revenue_category}
                onChange={(e) => setForm((f) => ({ ...f, revenue_category: e.target.value }))}
              />
            </IpdFormField>
            <IpdFormField label="Financial Reporting Category">
              <IpdTextField
                fullWidth
                value={form.report_category}
                onChange={(e) => setForm((f) => ({ ...f, report_category: e.target.value }))}
              />
            </IpdFormField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <IpdButton
            disabled={!form.name.trim()}
            onClick={async () => {
              if (report(await createBillingParameter(form))) {
                setOpen(false);
                setForm({ ...form, name: "", service_code: "" });
                onDone();
              }
            }}
          >
            Save Tariff Mapping
          </IpdButton>
        </DialogActions>
      </Dialog>
    </MasterList>
  );
}

function MaintenanceTab({ logs, rooms, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    operating_room_id: "",
    log_type: "FUMIGATION",
    outcome: "",
    notes: "",
  });

  return (
    <MasterList
      title="Terminal Cleaning & Sterilization Log"
      subtitle="Documentation of terminal cleaning, fumigation cycles, autoclave validation, and biomedical engineering maintenance."
      addLabel="Log Sterilization Cycle"
      onAdd={() => setOpen(true)}
    >
      {logs.length === 0 ? (
        <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13.5 }}>
          No sterilization or maintenance cycles recorded.
        </Typography>
      ) : (
        logs.map((l) => (
          <Row
            key={l.id}
            primary={`${l.operating_room_name} — ${l.log_type}`}
            secondary={`${formatIstDateTime(l.performed_at)}${
              l.performed_by_name ? ` · Verified by: ${l.performed_by_name}` : ""
            }${l.outcome ? ` · Outcome: ${l.outcome}` : ""}${l.notes ? ` · Notes: ${l.notes}` : ""}`}
          />
        ))
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Log Environmental Sterilization & Fumigation Cycle</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 0.5 }}>
            <IpdFormField label="Operating Suite">
              <OtSelect
                placeholder="Select Operating Theatre"
                value={form.operating_room_id}
                onChange={(e) => setForm((f) => ({ ...f, operating_room_id: e.target.value }))}
                options={rooms.map((r) => ({ value: r.id, label: r.name }))}
              />
            </IpdFormField>
            <IpdFormField label="Sterilization / Maintenance Procedure">
              <OtSelect
                value={form.log_type}
                onChange={(e) => setForm((f) => ({ ...f, log_type: e.target.value }))}
                options={MAINTENANCE_TYPES}
              />
            </IpdFormField>
            <IpdFormField label="Biological / Chemical Indicator Verification Outcome">
              <IpdTextField
                fullWidth
                value={form.outcome}
                onChange={(e) => setForm((f) => ({ ...f, outcome: e.target.value }))}
              />
            </IpdFormField>
            <IpdFormField label="Clinical Observations / Cycle Details">
              <IpdTextField
                fullWidth
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </IpdFormField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <IpdButton
            disabled={!form.operating_room_id}
            onClick={async () => {
              if (report(await recordMaintenance(form))) {
                setOpen(false);
                onDone();
              }
            }}
          >
            Record Cycle
          </IpdButton>
        </DialogActions>
      </Dialog>
    </MasterList>
  );
}


/**
 * The theatre environment register (NABH).
 *
 * Temperature and humidity daily, pressure differential at least monthly. The
 * maintenance log above covers the EVENTS — cleaning, fumigation, sterilisation
 * — and this covers the standing readings, which is usually the first register
 * an assessor asks to see and the one a theatre is least likely to have.
 *
 * COVERAGE IS THE HEADLINE, not the readings. The finding at an assessment is
 * almost never a bad reading — those get noticed and fixed on the day. It is a
 * day nobody wrote anything down, which is invisible in a list of readings and
 * obvious in a list of gaps. So the gaps come first.
 *
 * Whether a reading is in range is decided by the server and shown here; it is
 * never computed in the browser, for the same reason the count result is not.
 */
function EnvironmentTab({ environment, rooms, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    operating_room_id: "",
    temperature_c: "",
    humidity_pct: "",
    pressure_differential_pa: "",
    air_changes_per_hour: "",
    action_taken: "",
  });

  const rows = environment?.rows || [];
  const coverage = environment?.coverage || [];
  const gaps = coverage.filter((c) => !c.complete);
  const bands = environment?.bands || {};

  const empty = ["temperature_c", "humidity_pct", "pressure_differential_pa", "air_changes_per_hour"]
    .every((k) => !String(form[k]).trim());

  return (
    <MasterList
      title="Operating Theatre Environmental Monitoring Log"
      subtitle="NABH/AAMI standard environmental monitoring: Temperature (20–24°C), relative humidity (50–60%), and positive pressure differentials."
      addLabel="Log Environmental Reading"
      onAdd={() => setOpen(true)}
    >
      {gaps.length > 0 && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 1,
            backgroundColor: IPD_COLORS.warning.bg,
            color: IPD_COLORS.warning.text,
            fontSize: 13,
          }}
        >
          <strong>Non-Compliant Days (Missing Environmental Surveillance)</strong>
          {gaps.map((c) => (
            <Box key={c.operating_room_id} sx={{ mt: 0.5 }}>
              {c.operating_room_name}: {c.missing_days.join(", ")}
            </Box>
          ))}
        </Box>
      )}

      {environment?.breach_count > 0 && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 1,
            backgroundColor: IPD_COLORS.error.bg,
            color: IPD_COLORS.error.text,
            fontWeight: 700,
            fontSize: 13,
          }}
        >
          {environment.breach_count} environmental parameter(s) out of regulatory range over this monitoring period.
        </Box>
      )}

      {bands.temperature_c && (
        <Typography sx={{ mb: 1.5, fontSize: 12, color: IPD_COLORS.textSecondary }}>
          Regulatory Compliance Standards: {bands.temperature_c[0]}&ndash;{bands.temperature_c[1]}&deg;C,{" "}
          {bands.humidity_pct[0]}&ndash;{bands.humidity_pct[1]}% RH, min.{" "}
          {bands.pressure_differential_pa_min} Pa positive pressure differential.
        </Typography>
      )}

      {rows.length === 0 ? (
        <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13.5 }}>
          No environmental parameters logged for today.
        </Typography>
      ) : (
        rows.map((r) => (
          <Row
            key={r.id}
            primary={`${r.operating_room_name} — ${r.reading_date}`}
            secondary={
              [
                r.temperature_c ? `${r.temperature_c}\u00b0C` : null,
                r.humidity_pct ? `${r.humidity_pct}% RH` : null,
                r.pressure_differential_pa ? `${r.pressure_differential_pa} Pa` : null,
                r.air_changes_per_hour ? `${r.air_changes_per_hour} ACH` : null,
                r.recorded_by_name ? `Recorded by: ${r.recorded_by_name}` : null,
                r.within_range === false ? `OUT OF RANGE — ${r.out_of_range_note}` : null,
                r.action_taken ? `Corrective Action: ${r.action_taken}` : null,
              ]
                .filter(Boolean)
                .join(" · ")
            }
          />
        ))
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Log Operating Theatre Environmental Parameters</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 0.5 }}>
            <Box sx={{ gridColumn: "span 2" }}>
              <IpdFormField label="Operating Suite">
                <OtSelect
                  placeholder="Select Operating Theatre"
                  value={form.operating_room_id}
                  onChange={(e) => setForm((f) => ({ ...f, operating_room_id: e.target.value }))}
                  options={rooms.map((r) => ({ value: r.id, label: r.name }))}
                />
              </IpdFormField>
            </Box>
            <IpdFormField label="Operating Room Temperature (&deg;C)">
              <IpdTextField
                fullWidth
                value={form.temperature_c}
                onChange={(e) => setForm((f) => ({ ...f, temperature_c: e.target.value }))}
              />
            </IpdFormField>
            <IpdFormField label="Relative Humidity (% RH)">
              <IpdTextField
                fullWidth
                value={form.humidity_pct}
                onChange={(e) => setForm((f) => ({ ...f, humidity_pct: e.target.value }))}
              />
            </IpdFormField>
            <IpdFormField label="Positive Pressure Differential (Pa)">
              <IpdTextField
                fullWidth
                placeholder="Monthly verification reading (optional on routine daily logs)"
                value={form.pressure_differential_pa}
                onChange={(e) =>
                  setForm((f) => ({ ...f, pressure_differential_pa: e.target.value }))
                }
              />
            </IpdFormField>
            <IpdFormField label="Air Changes Per Hour (ACH)">
              <IpdTextField
                fullWidth
                value={form.air_changes_per_hour}
                onChange={(e) => setForm((f) => ({ ...f, air_changes_per_hour: e.target.value }))}
              />
            </IpdFormField>
            <Box sx={{ gridColumn: "span 2" }}>
              <IpdFormField label="Corrective Action Taken for Regulatory Variance">
                <IpdTextField
                  fullWidth
                  value={form.action_taken}
                  onChange={(e) => setForm((f) => ({ ...f, action_taken: e.target.value }))}
                />
              </IpdFormField>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <IpdButton
            disabled={!form.operating_room_id || empty}
            onClick={async () => {
              if (report(await recordEnvironmentReading(form))) {
                setOpen(false);
                onDone();
              }
            }}
          >
            Log Parameters
          </IpdButton>
        </DialogActions>
      </Dialog>
    </MasterList>
  );
}

/**
 * Note templates — standardized narrative documentation applied on demand.
 */
function TemplatesTab({ templates, availableFields, onDone }) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("surgical");
  const [name, setName] = useState("");
  const [fields, setFields] = useState({});
  const [available, setAvailable] = useState([]);
  const [busy, setBusy] = useState(false);

  // Which fields this note kind has. Served with the templates rather than
  // hard-coded, so a field added to the backend appears here without a change
  // and a template can never carry one the note does not have.
  useEffect(() => {
    setAvailable(availableFields[kind] || []);
    setFields({});
  }, [kind, availableFields]);

  const save = async () => {
    const filled = Object.fromEntries(
      Object.entries(fields).filter(([, v]) => String(v).trim())
    );
    if (!name.trim() || !Object.keys(filled).length) return;
    setBusy(true);
    try {
      if (report(await createNoteTemplate({ name: name.trim(), note_kind: kind, fields: filled }))) {
        setOpen(false);
        setName("");
        setFields({});
        onDone();
      }
    } finally {
      setBusy(false);
    }
  };

  const byKind = {};
  (templates || []).forEach((t) => {
    (byKind[t.note_kind] = byKind[t.note_kind] || []).push(t);
  });

  return (
    <MasterList
      title="Standardized Clinical Note Templates"
      subtitle="Pre-configured clinical documentation templates for standardized surgical, anesthetic, and nursing narrative entry."
      addLabel="Create Note Template"
      onAdd={() => setOpen(true)}
    >
      {(templates || []).length === 0 ? (
        <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13.5 }}>
          No standardized note templates configured.
        </Typography>
      ) : (
        ["surgical", "anaesthesia", "nursing"].map((k) =>
          byKind[k] ? (
            <Box key={k} sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontSize: 11.5,
                  fontWeight: 800,
                  letterSpacing: "0.04em",
                  color: IPD_COLORS.textSecondary,
                  mb: 0.5,
                }}
              >
                {k === "surgical"
                  ? "SURGICAL OPERATIVE NOTE TEMPLATES"
                  : k === "anaesthesia"
                  ? "ANESTHESIA RECORD TEMPLATES"
                  : "PERIOPERATIVE NURSING TEMPLATES"}
              </Typography>
              {byKind[k].map((t) => (
                <Row
                  key={t.id}
                  primary={t.name}
                  secondary={`${Object.keys(t.fields || {}).length} clinical field(s) populated`}
                  onDeactivate={async () => {
                    if (report(await retireNoteTemplate(t.id))) onDone();
                  }}
                  deactivateLabel="Retire Template"
                />
              ))}
            </Box>
          ) : null
        )
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Create Standardized Clinical Note Template</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 0.5 }}>
            <IpdFormField label="Template Title">
              <IpdTextField
                fullWidth
                placeholder="e.g. Total Knee Arthroplasty (Standard Protocol)"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </IpdFormField>
            <IpdFormField label="Clinical Note Discipline">
              <OtSelect
                value={kind}
                onChange={(e) => setKind(e.target.value)}
                options={[
                  { value: "surgical", label: "Surgical Operative Note" },
                  { value: "anaesthesia", label: "Anesthesia Intra-Operative Record" },
                  { value: "nursing", label: "Perioperative Nursing Record" },
                ]}
              />
            </IpdFormField>
          </Box>

          <Typography sx={{ mt: 2.5, mb: 1, fontSize: 12, fontWeight: 700 }}>
            DEFAULT CLINICAL TEXT (Fields left blank will not override clinical inputs upon insertion)
          </Typography>
          <Box sx={{ display: "grid", gap: 1.5 }}>
            {available.map((f) => (
              <IpdFormField key={f} label={humanise(f)}>
                <IpdTextField
                  fullWidth
                  multiline
                  minRows={1}
                  value={fields[f] || ""}
                  onChange={(e) => setFields((s) => ({ ...s, [f]: e.target.value }))}
                />
              </IpdFormField>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <IpdButton
            disabled={busy || !name.trim() || !Object.values(fields).some((v) => String(v).trim())}
            onClick={save}
          >
            Save Note Template
          </IpdButton>
        </DialogActions>
      </Dialog>
    </MasterList>
  );
}
